import {
  ApplicationConfig,
  isDevMode,
  APP_INITIALIZER,
  PLATFORM_ID,
  InjectionToken,
  ErrorHandler,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';

import { routes } from './app.routes';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideRouterStore } from '@ngrx/router-store';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { provideDevServiceWorkerCleanup } from './core/pwa/dev-service-worker-cleanup';
import {
  TranslateService,
  provideTranslateService,
  MissingTranslationHandler
} from '@ngx-translate/core';
import { provideRootTranslateLoader } from './app.translation';
import { httpErrorInterceptor } from './core/interceptors/res.interceptor';
import { ssrApiSkipInterceptor } from './core/interceptors/ssr-api-skip.interceptor';
import { aiAssistantPremiumHttpInterceptor } from 'src/config/api/interceptors/ai-assistant-premium.interceptor';
import { GlobalErrorHandler } from './core/config/error-handler.util';
import { AppMissingTranslationHandler } from './shared/utils/missing-translation.handler';

import { localStorageSyncMetaReducer } from 'src/state/local-storage.metareducer';
import { UserObjectReducer } from 'src/state/user/user.reducer';
import { studentReducer } from 'src/state/student/student.reducer';
import {
  otherClassesReducer,
  periodReducer,
} from 'src/state/class/class.reducer';
import { assessmentReducer } from 'src/state/assessment/assessment/assessment.reducer';

import { languageReducer } from 'src/state/language/language.reducer';
import { bulkAssessmentReducer } from 'src/state/bulk-assessment/bulk-assessment.reducer';
import { multiAssessmentReducer } from 'src/state/multi-assessment/multi-assessment.reducer';
import { UserEffects } from './core/state/user/user.effects';
import { isPlatformBrowser } from '@angular/common';
import { normalizeLanguageCode } from 'src/admin/utils/common/languages';
import { provideOfflineDatabase } from './core/offline/offline.providers';
import { offlineInterceptor } from './core/offline/http/offline.interceptor';

// import { provideMarkdown } from 'ngx-markdown';

// Translation initializer factory
export function appInitializerFactory(
  translate: TranslateService,
  platformId: Object
) {
  return () => {
    return new Promise<void>(resolve => {
      // Only run language logic in the browser
      if (!isPlatformBrowser(platformId)) {
        translate.setFallbackLang('en');
        translate.use('en').subscribe(() => resolve());
        return;
      }

      translate.setFallbackLang('en');
      let savedLanguage = 'en';

      // Check localStorage (NgRx persisted state)
      try {
        const state = localStorage.getItem('language');
        if (state && state !== 'undefined' && state !== 'null') {
          const parsed = JSON.parse(state);
          if (
            parsed &&
            parsed.selectedLanguage &&
            parsed.selectedLanguage !== '' &&
            parsed.selectedLanguage !== 'null'
          ) {
            savedLanguage = parsed.selectedLanguage;
            console.log('Language from NgRx store:', savedLanguage);
          }
        }
      } catch (e) {
        console.error('Error parsing language state:', e);
      }

      // Check for logged in user (teacher or admin)
      const user = localStorage.getItem('user') || sessionStorage.getItem('userData');
      const isLoggedIn = user && user !== 'null' && user !== 'undefined';

      // Try to get language from store, then localStorage
      const lsLang = localStorage.getItem('targetedLang');

      if (isLoggedIn && savedLanguage === 'en' && lsLang && lsLang !== '' && lsLang !== 'null') {
        savedLanguage = normalizeLanguageCode(lsLang);
      }

      // If still en and we are in browser, we can also check browser defaults if needed,
      // but usually we want to stick to what we found.

      translate.use(savedLanguage).subscribe(() => {
        console.log('Translations loaded:', savedLanguage);
        resolve();
      });
    });
  };
}
export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
    ),
    provideClientHydration(),
    provideAnimationsAsync(),
    provideStore(
      {
        userObject: UserObjectReducer,
        studentObject: studentReducer,
        periodClassInfo: periodReducer,
        otherClasses: otherClassesReducer,
        assessment: assessmentReducer,
        language: languageReducer,
        bulkAssessment: bulkAssessmentReducer,
        multiAssessment: multiAssessmentReducer,
      },
      { metaReducers: [localStorageSyncMetaReducer] }
    ),
    // ssrApiSkipInterceptor runs first so the error interceptor never sees the 401s that
    // server-side calls used to produce.
    // offlineInterceptor runs last, closest to the network: the API when it is reachable,
    // local IndexedDB when it is not. A write it stores offline reaches httpErrorInterceptor
    // as a success, so no network-error toast or redirect fires for work that was saved.
    provideHttpClient(
      withFetch(),
      withInterceptors([ssrApiSkipInterceptor, httpErrorInterceptor, offlineInterceptor])
    ),
    // Opens the Dexie/IndexedDB store and starts background synchronisation.
    // Browser-only and non-blocking: a browser that refuses IndexedDB still
    // gets a working online app.
    provideOfflineDatabase(),
    provideTranslateService({
      loader: provideRootTranslateLoader(),
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: AppMissingTranslationHandler,
      },
      fallbackLang: 'en',
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, PLATFORM_ID as InjectionToken<Object>],
      multi: true,
    },
    provideEffects([UserEffects]),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    provideRouterStore(),
    provideServiceWorker('ngsw-worker.js', {
      // Deployed builds only. Under `ng serve` every rebuild re-hashes the
      // chunks while the installed worker still holds the ngsw.json it cached,
      // so the two disagree on the first file it checks and the app dies with
      //
      //   SwCriticalError: Hash mismatch (cacheBustedFetchFromNetwork):
      //   /chunk-XXXX.js: expected <a>, got <b> (after cache busting)
      //
      // It repeats because the worker survives a reload: it re-checks, mismatches
      // again, and takes the page down again.
      //
      // This flag is false in exactly the two configurations `ng serve` uses
      // (local, development) and true in the four that are deployed (production,
      // stage, fastrack, ftkpreprod), so the worker keeps doing its job wherever
      // it is actually wanted.
      enabled: true,
      registrationStrategy: 'registerImmediately',
    }),
    // Disabling registration does not remove a worker already installed on a
    // developer machine, and that worker is what produces the hash-mismatch
    // crash. This clears it - production is left alone.
    provideDevServiceWorkerCleanup(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
};
